import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Banknote, Camera, Check, Clapperboard, Eye, FileCheck2, FileText, FolderOpen, Hourglass, Landmark, Paperclip, Receipt, Ruler, Search, ShieldCheck, X, type LucideIcon } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useDialog } from '../lib/useDialog';
import { formatUsd, localeUI } from '../lib/format';
import { agruparAdjuntos, coincideAdjunto, esAdjuntable, montoDeAdjunto, type AttachableFile, type GrupoAdjunto } from '../lib/adjuntos';
import { FileViewerModal } from './FileViewerModal';

/**
 * LA VENTANA PARA ADJUNTAR ARCHIVOS DEL CLAIM A UN CORREO (dueño 2026-10-28).
 *
 * Reporte de Gabriela: no aparecían los invoices (nunca estaban en Files: mig 0552) y el selector abría
 * diminuto. Pedido textual: "que le des clic y abra una pantalla en el medio, en el centro, un poco más
 * grande, donde puedas ver qué es, qué tipo de files, organizados por estimados, invoices, contratistas,
 * lo que sea, bien organizado, y sus montos a la derecha; y cuándo se subió y quién. Eso es para todos los
 * emails". La usan los tres composes: la ventana del claim (`ClaimEmailBox`), el composer de comms
 * (`ClaimComms`) y la respuesta de la bandeja del departamento (`DepartmentInbox`).
 *
 * Carga la lista ella misma (así trae lo recién subido y los invoices) y devuelve los archivos elegidos
 * con `onAttach`: el padre suma los ids a su lista y, si quiere, los objetos a su `files` para pintar el
 * nombre en el chip. Se eligen varios de una, con buscador, y el 👁 abre la vista previa sin cerrar.
 */
const META: Record<GrupoAdjunto, { key: string; icon: LucideIcon; color: string }> = {
  proactive: { key: 'att.gProactive', icon: Ruler, color: '#166534' },
  insurance: { key: 'att.gInsurance', icon: Landmark, color: '#b45309' },
  invoices: { key: 'att.gInvoices', icon: Receipt, color: '#0369a1' },
  contracts: { key: 'att.gContracts', icon: FileText, color: '#7c3aed' },
  checks: { key: 'att.gChecks', icon: Banknote, color: '#0e7490' },
  coc: { key: 'att.gCoc', icon: FileCheck2, color: '#166534' },
  policy: { key: 'att.gPolicy', icon: ShieldCheck, color: '#1d4ed8' },
  photos: { key: 'att.gPhotos', icon: Camera, color: '#b45309' },
  atr: { key: 'att.gAtr', icon: Clapperboard, color: '#9d174d' },
  other: { key: 'att.gOther', icon: FolderOpen, color: '#6b7280' },
};

export function AttachFilesModal({ claimId, selected, onAttach, onClose }: {
  claimId: string;
  /** Ids que el correo YA lleva: salen marcados y no se vuelven a ofrecer. */
  selected: string[];
  onAttach: (files: AttachableFile[]) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose, { autoFocus: false });
  const [files, setFiles] = useState<AttachableFile[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [elegidos, setElegidos] = useState<Set<string>>(() => new Set());
  const [preview, setPreview] = useState<AttachableFile | null>(null);

  useEffect(() => {
    let vivo = true;
    api.get<{ files: AttachableFile[] }>(`/api/claims/${claimId}/files`)
      .then((r) => { if (vivo) setFiles((r.files || []).filter(esAdjuntable)); })
      .catch(() => { if (vivo) setError(true); });
    return () => { vivo = false; };
  }, [claimId]);

  const grupos = useMemo(() => agruparAdjuntos((files ?? []).filter((f) => coincideAdjunto(f, q))), [files, q]);
  const yaVa = (id: string) => selected.includes(id);
  const toggle = (f: AttachableFile) => {
    if (yaVa(f.id)) return;
    setElegidos((s) => { const n = new Set(s); if (n.has(f.id)) n.delete(f.id); else n.add(f.id); return n; });
  };
  const confirmar = () => {
    const lista = (files ?? []).filter((f) => elegidos.has(f.id));
    if (lista.length) onAttach(lista);
    onClose();
  };

  const fecha = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(localeUI(), { year: 'numeric', month: 'short', day: 'numeric' }) : null);
  /** "por Gabriela" · "desde un correo: …" · "desde JobFlow" · "generado por el sistema". */
  const origen = (f: AttachableFile): string | null => {
    if (f.origin_kind === 'email') return f.origin_detail ? `${t('att.fromEmail')}: ${f.origin_detail}` : t('att.fromEmail');
    if (f.origin_kind === 'jobflow') return f.source_tag || t('att.fromJobflow');
    if (f.origin_kind === 'monday') return 'Monday';
    if (f.origin_kind === 'system') return t('att.generated');
    return f.uploaded_by ? t('att.by', { name: f.uploaded_by }) : null;
  };
  const tipo = (f: AttachableFile): string => {
    if (f.invoice?.number) return f.invoice.number;
    if (f.contractor_invoice_of) return `${t('att.contractorInvoice')} · ${f.contractor_invoice_of}`;
    return f.doc_label || (f.doc_type ?? '').replace(/_/g, ' ') || t('em.kindDoc');
  };

  const total = elegidos.size;
  const ui = (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={dialogo.ref} {...dialogo.props} className="card" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(960px, 96vw)', height: 'min(82vh, 780px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Encabezado: título, cuántos van, buscador y cerrar. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          <b style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-md)' }}>
            <Paperclip size={16} strokeWidth={1.75} /> {t('att.title')}
          </b>
          {files && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('att.count', { n: String(files.length) })}</span>}
          <span style={{ flex: 1 }} />
          <label style={{ position: 'relative', minWidth: 220 }}>
            <Search size={13} strokeWidth={1.75} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
            <input className="input sm" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('att.searchPh')} style={{ paddingLeft: 26, width: '100%' }} />
          </label>
          <button type="button" className="calx-pop-x" onClick={onClose} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button>
        </div>

        {/* Cuerpo: los grupos, cada uno con su encabezado y sus filas. */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 10px 10px' }}>
          {error ? (
            <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{t('fv.error')}</div>
          ) : !files ? (
            <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>
          ) : grupos.length === 0 ? (
            <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{q ? t('att.noMatch') : t('em.noFiles')}</div>
          ) : grupos.map(({ grupo, files: lista }) => {
            const m = META[grupo];
            return (
              <section key={grupo} style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1, borderBottom: '1px solid var(--line)' }}>
                  <span style={{ color: m.color, display: 'inline-flex' }}><m.icon size={15} strokeWidth={1.75} /></span>
                  <b style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: 0.4, color: m.color }}>{t(m.key)}</b>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{lista.length}</span>
                </div>
                {lista.map((f) => {
                  const va = yaVa(f.id);
                  const marcado = va || elegidos.has(f.id);
                  const amt = montoDeAdjunto(f);
                  const anulado = f.invoice?.status === 'void';
                  return (
                    <div key={f.id} role="button" tabIndex={0} onClick={() => toggle(f)}
                      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(f); } }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px', borderRadius: 8, cursor: va ? 'default' : 'pointer', background: marcado && !va ? 'var(--accent-soft)' : 'transparent', opacity: va ? 0.6 : 1 }}
                      onMouseEnter={(e) => { if (!marcado) e.currentTarget.style.background = 'var(--surface-2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = marcado && !va ? 'var(--accent-soft)' : 'transparent'; }}>
                      <input type="checkbox" checked={marcado} disabled={va} readOnly tabIndex={-1} onClick={(e) => e.stopPropagation()} onChange={() => toggle(f)} />
                      <span style={{ width: 20, display: 'inline-flex', justifyContent: 'center', flexShrink: 0, color: m.color }}><m.icon size={16} strokeWidth={1.75} /></span>
                      <span style={{ display: 'grid', gap: 2, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: anulado ? 'line-through' : undefined }} title={f.file_name ?? undefined}>
                          {f.file_name}
                        </span>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-2xs)' }}>
                          <span className="badge" style={{ color: m.color, borderColor: m.color, background: 'transparent' }}>{tipo(f)}</span>
                          {anulado && <span className="badge" style={{ background: 'var(--red-soft)', color: 'var(--red)' }}>{t('att.voided')}</span>}
                          {f.supplement_status && (
                            <span className="badge" style={{ background: f.supplement_status.kind === 'resolved' ? 'var(--green-soft)' : 'var(--orange-soft)', color: f.supplement_status.kind === 'resolved' ? 'var(--green)' : 'var(--orange)' }}>
                              {f.supplement_status.kind === 'resolved' ? <><Check size={11} strokeWidth={1.75} /> {t('sup.resolved')}</> : <><Hourglass size={11} strokeWidth={1.75} /> {t('sup.preApproved')}</>}
                            </span>
                          )}
                          {fecha(f.created_at) && <span className="muted">{fecha(f.created_at)}</span>}
                          {origen(f) && <span className="muted">· {origen(f)}</span>}
                          {va && <span style={{ color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Check size={11} strokeWidth={1.75} /> {t('att.already')}</span>}
                        </span>
                      </span>
                      {amt != null && (
                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font-mono)', color: 'var(--accent)', flexShrink: 0, minWidth: 92, textAlign: 'right' }}>{formatUsd(amt)}</span>
                      )}
                      {!f.external_url && (
                        <button type="button" className="btn ghost sm" title={t('att.preview')} style={{ padding: '2px 7px', flexShrink: 0 }}
                          onClick={(e) => { e.stopPropagation(); setPreview(f); }}>
                          <Eye size={13} strokeWidth={1.75} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>

        {/* Pie: cuántos elegidos, cancelar, adjuntar. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--line)' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{total > 0 ? t('att.selected', { n: String(total) }) : t('att.footHint')}</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn sm" disabled={total === 0} onClick={confirmar}>
            <Paperclip size={13} strokeWidth={1.75} /> {total > 1 ? t('att.attachN', { n: String(total) }) : t('att.attachOne')}
          </button>
        </div>
      </div>
      {preview && (
        <FileViewerModal
          fileId={preview.id}
          title={preview.file_name}
          onClose={() => setPreview(null)}
          actions={!yaVa(preview.id) && !elegidos.has(preview.id) ? (
            <button type="button" className="btn sm" onClick={() => { toggle(preview); setPreview(null); }}>
              <Paperclip size={13} strokeWidth={1.75} /> {t('att.select')}
            </button>
          ) : undefined}
        />
      )}
    </div>
  );
  return createPortal(ui, document.body);
}
